import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { LegalConsent } from './LegalDocuments';
import { Mail, Lock, Eye, EyeOff, User, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Helper functions for returning user detection
const RETURNING_USER_KEY = 'skillsos_returning_user';
const LAST_EMAIL_KEY = 'skillsos_last_email';

const isReturningUser = () => {
  try {
    return localStorage.getItem(RETURNING_USER_KEY) === 'true';
  } catch {
    return false;
  }
};

const markAsReturningUser = () => {
  try {
    localStorage.setItem(RETURNING_USER_KEY, 'true');
  } catch {
    // localStorage not available
  }
};

const getLastEmail = () => {
  try {
    return localStorage.getItem(LAST_EMAIL_KEY) || '';
  } catch {
    return '';
  }
};

const saveLastEmail = (email) => {
  try {
    localStorage.setItem(LAST_EMAIL_KEY, email);
  } catch {
    // localStorage not available
  }
};

export const AuthModal = ({ 
  open, 
  onOpenChange, 
  defaultMode = 'login',
  onSuccess 
}) => {
  const { t } = useTranslation();
  const { login, register, checkEmailExists } = useAuth();
  
  // Check if this is a returning user on this device
  const [isReturning, setIsReturning] = useState(false);
  
  // Multi-step flow: 'email' -> 'login' or 'register' or 'forgot'
  const [step, setStep] = useState('email');
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [legalConsent, setLegalConsent] = useState({
    terms_accepted: false,
    privacy_accepted: false,
    marketing_consent: false
  });

  const [errors, setErrors] = useState({});

  // Check if returning user when modal opens
  useEffect(() => {
    if (open) {
      const returning = isReturningUser();
      setIsReturning(returning);
      
      // Pre-fill email if returning user
      if (returning) {
        const lastEmail = getLastEmail();
        if (lastEmail) {
          setFormData(prev => ({ ...prev, email: lastEmail }));
        }
      }
    }
  }, [open]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setStep('email');
      setIsNewUser(false);
      setResetEmailSent(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      setErrors({});
      setLegalConsent({
        terms_accepted: false,
        privacy_accepted: false,
        marketing_consent: false
      });
    }
  }, [open]);

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setErrors({ email: t('emailRequired') || 'Имейлът е задължителен' });
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: formData.email });
      setResetEmailSent(true);
      toast.success(t('resetEmailSent') || 'Изпратихме ви имейл с инструкции за нова парола');
    } catch (error) {
      // Don't reveal if email exists or not for security
      setResetEmailSent(true);
      toast.success(t('resetEmailSent') || 'Ако имейлът съществува, ще получите инструкции');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Step 1: Check if email exists
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setErrors({ email: t('emailRequired') });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors({ email: t('emailInvalid') });
      return;
    }

    setCheckingEmail(true);
    
    try {
      // Check if email exists in the system
      const exists = await checkEmailExists(formData.email);
      setIsNewUser(!exists);
      setStep(exists ? 'login' : 'register');
    } catch (error) {
      // If check fails, default to showing registration
      console.error('Email check failed:', error);
      setIsNewUser(true);
      setStep('register');
    } finally {
      setCheckingEmail(false);
    }
  };

  // Step 2: Login
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.password) {
      setErrors({ password: t('passwordRequired') });
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Mark as returning user and save email for next time
        markAsReturningUser();
        saveLastEmail(formData.email);
        
        onOpenChange(false);
        if (onSuccess) {
          onSuccess(result.user);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Register
  const handleRegister = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = t('nameRequired');
    }
    
    if (!formData.password) {
      newErrors.password = t('passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('passwordMinLength');
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwordMismatch');
    }
    
    if (!legalConsent.terms_accepted || !legalConsent.privacy_accepted) {
      newErrors.legal = t('legalRequired') || 'Моля, приемете условията';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        terms_accepted: legalConsent.terms_accepted,
        privacy_accepted: legalConsent.privacy_accepted,
        marketing_consent: legalConsent.marketing_consent
      };
      
      const result = await register(registerData);
      
      if (result.success) {
        // Mark as returning user and save email for next time
        markAsReturningUser();
        saveLastEmail(formData.email);
        
        onOpenChange(false);
        if (onSuccess) {
          onSuccess(result.user, true); // true = isNewUser (just registered)
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('email');
    setErrors({});
  };

  // Handle Google Sign In
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    // Store current page URL so user returns here after Google login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/' && currentPath !== '/home') {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }
    const redirectUrl = window.location.origin + '/auth-callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden max-h-[90vh] flex flex-col">
        <DialogTitle className="sr-only">SkillSOS Authentication</DialogTitle>
        {/* Header with Logo */}
        <div className="bg-white px-6 py-6 text-center flex-shrink-0 border-b">
          {/* Progress Indicator */}
          <div className="flex justify-center items-center gap-2 mb-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step === 'email' ? 'bg-purple-600 text-white' : 'bg-green-500 text-white'
            }`}>
              {step === 'email' ? '1' : '✓'}
            </div>
            <div className={`w-12 h-1 rounded ${step === 'email' ? 'bg-gray-200' : 'bg-purple-600'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step === 'email' ? 'bg-gray-200 text-gray-500' : 'bg-purple-600 text-white'
            }`}>
              2
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            {step === 'email' 
              ? (t('step1of2') || 'Стъпка 1 от 2') 
              : (t('step2of2') || 'Стъпка 2 от 2')}
          </p>
          
          <img 
            src="/skillsos-logo.png" 
            alt="SkillSOS" 
            className="h-24 mx-auto mb-3"
          />
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'email' && (isReturning 
              ? (t('welcomeBack') || 'Добре дошли отново!') 
              : (t('welcomeToSkillSOS') || 'Добре дошли в SkillSOS'))}
            {step === 'login' && (t('welcomeBack') || 'Добре дошли отново!')}
            {step === 'register' && (t('createAccount') || 'Създайте профил')}
            {step === 'forgot' && (t('resetPassword') || 'Възстановяване на парола')}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {step === 'email' && (isReturning 
              ? (t('enterEmailToLogin') || 'Въведете имейл, за да влезете')
              : (t('enterEmailToContinue') || 'Въведете имейл, за да продължите'))}
            {step === 'login' && formData.email}
            {step === 'register' && formData.email}
            {step === 'forgot' && (t('enterEmailForReset') || 'Въведете имейла си')}
          </p>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Step 1: Email Entry */}
          {step === 'email' && (
            <>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="sr-only">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      data-testid="auth-email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      placeholder={t('emailPlaceholder') || 'Вашият имейл адрес'}
                      className={`pl-10 h-12 text-base ${errors.email ? 'border-red-500' : ''}`}
                      autoFocus
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <Button
                  type="submit"
                  data-testid="auth-continue"
                  className="w-full h-12 text-base bg-purple-600 hover:bg-purple-700"
                  disabled={checkingEmail}
                >
                  {checkingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('checking') || 'Проверка...'}
                    </>
                  ) : (
                    t('continue') || 'Продължи'
                  )}
                </Button>
              </form>

              {/* Quick signup prompt */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-sm text-green-800 font-medium" data-testid="quick-signup-hint">
                  {t('quickSignupHint') || 'Регистрирай се за 10 секунди, за да завършиш резервацията'}
                </p>
              </div>

              {/* Show register option - always available for new users on shared devices */}
              <p className="text-center text-sm text-gray-500 pt-2">
                {isReturning ? (
                  <>
                    {t('differentUser') || 'Друг потребител?'}{' '}
                    <button 
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, email: '' }));
                        // Clear the pre-filled email so they can enter a new one
                      }}
                      className="text-purple-600 hover:underline font-medium"
                    >
                      {t('useNewEmail') || 'Въведете нов имейл'}
                    </button>
                  </>
                ) : (
                  <>
                    {t('alreadyHaveAccount') || 'Вече имате профил?'}{' '}
                    <span className="text-gray-600">
                      {t('justEnterEmail') || 'Просто въведете имейла си'}
                    </span>
                  </>
                )}
              </p>
            </>
          )}

          {/* Step 2a: Login (existing user) */}
          {step === 'login' && (
            <>
              <button 
                onClick={goBack}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('changeEmail') || 'Промени имейла'}
              </button>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="password" className="sr-only">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      data-testid="auth-password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      placeholder={t('passwordPlaceholder') || 'Вашата парола'}
                      className={`pl-10 pr-10 h-12 text-base ${errors.password ? 'border-red-500' : ''}`}
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                </div>

                <Button
                  type="submit"
                  data-testid="auth-login"
                  className="w-full h-12 text-base bg-purple-600 hover:bg-purple-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('signingIn') || 'Влизане...'}
                    </>
                  ) : (
                    t('signIn') || 'Вход'
                  )}
                </Button>
                
                {/* Forgot Password Link */}
                <div className="text-center">
                  <button 
                    type="button"
                    onClick={() => setStep('forgot')}
                    className="text-sm text-purple-600 hover:underline"
                  >
                    {t('forgotPassword') || 'Забравена парола?'}
                  </button>
                </div>
              </form>

              <p className="text-center text-sm text-gray-500">
                {t('noAccount') || 'Нямате профил?'}{' '}
                <button 
                  onClick={() => { setStep('register'); setIsNewUser(true); }}
                  className="text-purple-600 hover:underline font-medium"
                >
                  {t('createOne') || 'Създайте'}
                </button>
              </p>
            </>
          )}

          {/* Forgot Password Step */}
          {step === 'forgot' && (
            <>
              <button 
                onClick={() => setStep('login')}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('backToLogin') || 'Обратно към вход'}
              </button>

              {resetEmailSent ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('checkYourEmail') || 'Проверете имейла си'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t('resetInstructions') || 'Изпратихме инструкции за възстановяване на паролата на'} <strong>{formData.email}</strong>
                  </p>
                  <Button
                    onClick={() => { setStep('login'); setResetEmailSent(false); }}
                    className="mt-6 bg-purple-600 hover:bg-purple-700"
                  >
                    {t('backToLogin') || 'Обратно към вход'}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('resetPassword') || 'Възстановяване на парола'}
                    </h3>
                    <p className="text-gray-600 text-sm mt-1">
                      {t('enterEmailForReset') || 'Въведете имейла си и ще ви изпратим линк за нова парола'}
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="reset-email" className="sr-only">{t('email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="reset-email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        placeholder={t('emailPlaceholder') || 'вашият@имейл.com'}
                        className={`pl-10 h-12 text-base ${errors.email ? 'border-red-500' : ''}`}
                        autoFocus
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base bg-purple-600 hover:bg-purple-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('sending') || 'Изпращане...'}
                      </>
                    ) : (
                      t('sendResetLink') || 'Изпрати линк за нова парола'
                    )}
                  </Button>
                </form>
              )}
            </>
          )}

          {/* Step 2b: Register (new user) */}
          {step === 'register' && (
            <>
              <button 
                onClick={goBack}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('changeEmail') || 'Промени имейла'}
              </button>

              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="sr-only">{t('fullName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="name"
                      data-testid="auth-name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      placeholder={t('fullNamePlaceholder') || 'Вашето име'}
                      className={`pl-10 h-12 text-base ${errors.name ? 'border-red-500' : ''}`}
                      autoFocus
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="reg-password" className="sr-only">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="reg-password"
                      data-testid="auth-reg-password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      placeholder={t('createPassword') || 'Създайте парола'}
                      className={`pl-10 pr-10 h-12 text-base ${errors.password ? 'border-red-500' : ''}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                </div>

                <div>
                  <Label htmlFor="confirm-password" className="sr-only">{t('confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="confirm-password"
                      data-testid="auth-confirm-password"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange('confirmPassword')}
                      placeholder={t('confirmPasswordPlaceholder') || 'Потвърдете паролата'}
                      className={`pl-10 h-12 text-base ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                </div>

                {/* Legal Consent - Compact */}
                <Card className="border-gray-200">
                  <CardContent className="p-3">
                    <LegalConsent 
                      consent={legalConsent} 
                      onConsentChange={setLegalConsent}
                      compact={true}
                    />
                    {errors.legal && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.legal}
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Button
                  type="submit"
                  data-testid="auth-register"
                  className="w-full h-12 text-base bg-purple-600 hover:bg-purple-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('creatingAccount') || 'Създаване...'}
                    </>
                  ) : (
                    t('createAccount') || 'Създай профил'
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-gray-500">
                {t('alreadyHaveAccount') || 'Вече имате профил?'}{' '}
                <button 
                  onClick={() => setStep('login')}
                  className="text-purple-600 hover:underline font-medium"
                >
                  {t('signIn') || 'Вход'}
                </button>
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
